import { Component, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import {
    ClaimService,
    LifecycleAware,
    Modal,
    ModalWorkflowService,
    getRouterLinkById
} from '@lsq-frontend/common-ui';
import {
    SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS,
    SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { RelationshipEnrollmentTotalsGql } from '@lsq-frontend/remote-services/financing-graphql';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { BuyerSupplierUploadModalComponent } from '../../buyer/upload/buyer-upload-modal/buyer-supplier-upload-modal.component';
import { SupplierUploadSuccessModal as BuyerSupplierUploadSuccessModal } from '../../buyer/upload/buyer-upload-modal/buyer-upload-success-modal/supplier-upload-success-modal';
import { BuyerAccountSupplierUploadModalComponent } from '../../upload/buyer-account-upload-modal/buyer-account-supplier-upload-modal.component';
import { SupplierUploadSuccessModal as AcctSupplierUploadSuccessModal } from '../../upload/buyer-account-upload-modal/buyer-account-upload-success-modal/supplier-upload-success-modal';

@LifecycleAware()
@Component({
    selector: 'account-supplier-summary-tile',
    styleUrls: ['./supplier-summary-tile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <card class="supplier-summary-card">
            <div card-header>
                <h3 id="supplier-summary-card-heading" i18n>Supplier Overview</h3>
            </div>
            <div class="supplier-summary-card-body" card-body>
                <lsq-circle-loader *ngIf="dataSource.loading$ | async"></lsq-circle-loader>
                <div *ngIf="!(dataSource.loading$ | async)">
                    <div class="supplier-summary-section">
                        <div class="supplier-summary-section-label" i18n>Enrolled</div>
                        <div class="supplier-summary-section-value">
                            {{ enrolledCount$ | async }}
                        </div>
                    </div>
                    <div class="supplier-summary-section">
                        <div class="supplier-summary-section-label" i18n>Not Enrolled</div>
                        <div class="supplier-summary-section-value">
                            {{ notEnrolledCount$ | async }}
                        </div>
                    </div>
                    <div class="supplier-summary-section">
                        <div class="supplier-summary-section-label" i18n>Invitations Sent</div>
                        <div class="supplier-summary-section-value">
                            {{ notEnrolledInvitedCount$ | async }}
                        </div>
                    </div>
                    <div class="supplier-summary-section">
                        <div class="supplier-summary-section-label" i18n>Total Suppliers</div>
                        <div class="supplier-summary-section-value">{{ totalCount$ | async }}</div>
                    </div>
                </div>
                <lsq-button
                    id="supplier-summary-upload-button"
                    size="md"
                    appearance="secondary"
                    (buttonClick)="handleUploadButtonClick()"
                >
                    Upload New Suppliers
                </lsq-button>
            </div>
        </card>
    `
})
export class AccountSupplierSummaryTileComponent implements OnChanges {
    @Input() public dataSource!: LsqDataSource<RelationshipEnrollmentTotalsGql>;
    @Input() public isBuyerAccount = false;

    public enrolledCount$!: Observable<number>;
    public notEnrolledCount$!: Observable<number>;
    public notEnrolledInvitedCount$!: Observable<number>;
    public totalCount$!: Observable<number>;

    public suppliersRouterLink$ = this._claimService
        .isOn(DashboardClaim.FinancingAccountPortal)
        .pipe(
            map((isAccountUser) =>
                getRouterLinkById(
                    atlas,
                    isAccountUser
                        ? SUPPLY_CHAIN_DIRECT_ACCOUNT_SUPPLIERS
                        : SUPPLY_CHAIN_DIRECT_BUYER_SUPPLIERS
                )
            )
        );

    public constructor(
        private _i18n: I18n,
        private _claimService: ClaimService,
        private _modalWorkflowService: ModalWorkflowService
    ) {}

    public ngOnChanges(): void {
        this.enrolledCount$ = this.dataSource.data$.pipe(pluck('enrolledCount'));
        this.notEnrolledCount$ = this.dataSource.data$.pipe(pluck('notEnrolledCount'));
        this.notEnrolledInvitedCount$ = this.dataSource.data$.pipe(
            pluck('notEnrolledInvitedCount')
        );
        this.totalCount$ = this.dataSource.data$.pipe(pluck('totalCount'));
    }

    public handleUploadButtonClick(): void {
        if (this.isBuyerAccount) {
            this._modalWorkflowService.showWorkflow([
                new Modal({
                    bodyComponent: BuyerAccountSupplierUploadModalComponent,
                    title: this._i18n('Upload Suppliers'),
                    id: 'buyer-account-upload',
                    showCloseButton: true,
                    showFooter: false,
                    showOkButton: false
                }),
                new AcctSupplierUploadSuccessModal()
            ]);
        } else {
            this._modalWorkflowService.showWorkflow([
                new Modal({
                    bodyComponent: BuyerSupplierUploadModalComponent,
                    title: this._i18n('Upload Suppliers'),
                    id: 'buyer-upload',
                    showCloseButton: true,
                    showFooter: false,
                    showOkButton: false
                }),
                new BuyerSupplierUploadSuccessModal()
            ]);
        }
    }
}
